import { useState, ChangeEvent } from "react";
import { MosaicElementType, uploadImage } from "../../../../api/mosaicApi";

export interface BaseWidgetData<TParams> {
    type: MosaicElementType;
    startColumn: number;
    width: number;
    startRow: number;
    height: number;
    name: string;
    parameters: TParams;
}

export interface WidgetPositionProps {
    columnStart: number | string;
    width: number | string;
    rowStart: number | string;
    height: number | string;
    onColumnStartChange: (value: number | string) => void;
    onWidthChange: (value: number | string) => void;
    onRowStartChange: (value: number | string) => void;
    onHeightChange: (value: number | string) => void;
    isOutOfLimit: boolean;
    maxElements: number;
    currentCount: number;
    validationError?: string;
}

export const makeNumericHandler = (setter: (value: number | string) => void) =>
    (value: string) => setter(value === '' ? '' : +value);

export function usePositionHandlers(props: {
    onColumnStartChange: (value: number | string) => void;
    onWidthChange: (value: number | string) => void;
    onRowStartChange: (value: number | string) => void;
    onHeightChange: (value: number | string) => void;
}) {
    const makeChangeHandler = (setter: (v: number | string) => void) =>
        (value: string) => {
            if (value === "" || +value < 1) setter('');
            else setter(+value);
        };

    const makeBlurHandler = (setter: (v: number | string) => void) =>
        (value: string) => { if (value === "") setter(1); };

    return {
        handleColumnStartChange: makeChangeHandler(props.onColumnStartChange),
        handleWidthChange: makeChangeHandler(props.onWidthChange),
        handleRowStartChange: makeChangeHandler(props.onRowStartChange),
        handleHeightChange: makeChangeHandler(props.onHeightChange),
        handleColumnStartBlur: makeBlurHandler(props.onColumnStartChange),
        handleWidthBlur: makeBlurHandler(props.onWidthChange),
        handleRowStartBlur: makeBlurHandler(props.onRowStartChange),
        handleHeightBlur: makeBlurHandler(props.onHeightChange),
    };
}

export function useImageUpload(opts?: {
    onUrlChange?: (url: string) => void;
    onSuccess?: (data: { name: string; url: string }) => void;
    validateFile?: (file: File) => boolean;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [imageName, setImageName] = useState('');
    const [imageUrl, setImageUrl] = useState('');

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const file = event.target.files[0];
        if (opts?.validateFile && !opts.validateFile(file)) {
            event.target.value = '';
            return;
        }
        const formData = new FormData();
        setIsLoading(true);
        formData.append("Recource", file);
        const res = await uploadImage(formData);
        if (res.status === 200) {
            setImageName(res.data.data.name);
            setImageUrl(res.data.data.url);
            opts?.onUrlChange?.(res.data.data.url);
            opts?.onSuccess?.({ name: res.data.data.name, url: res.data.data.url });
            event.target.value = '';
        }
        setIsLoading(false);
    };

    const reset = () => {
        setImageName('');
        setImageUrl('');
        opts?.onUrlChange?.('');
    };

    return { isLoading, imageName, imageUrl, handleFileChange, reset };
}
